import { Select } from '@/ui';
import type { OptionType } from '@/ui/components/Form/Select/types';

import type { AutocompleteValue } from '../../types';

export interface AutocompleteFieldProps {
  label: string;
  multiple?: boolean;
  value: AutocompleteValue | AutocompleteValue[] | null;
  onChange: (value: AutocompleteValue | AutocompleteValue[] | null) => void;
  error?: string;
  disabled?: boolean;
  options: OptionType<string>[];
  onSearch?: (query: string) => void;
  loading?: boolean;
  placeholder?: string;
  description?: string;
}

const toOption = (value: AutocompleteValue): OptionType<string> => ({
  value: value.id,
  label: value.label ?? value.id
});

const toValue = (option: OptionType<string>): AutocompleteValue => ({
  id: option.value,
  label: option.label
});

const AutocompleteField = ({
  label,
  multiple,
  value,
  onChange,
  error,
  disabled,
  options,
  onSearch,
  loading,
  placeholder,
  description
}: AutocompleteFieldProps) => {
  if (multiple) {
    const selected = Array.isArray(value) ? value : [];
    return (
      <Select
        multiple
        label={label}
        disabled={disabled}
        error={error}
        options={options}
        placeholder={placeholder}
        helpText={description}
        onSearch={onSearch}
        missingOptionsText={loading ? 'Loading…' : 'No matches'}
        selectedOptions={selected.map(toOption)}
        onChange={(next) => onChange(next.map(toValue))}
      />
    );
  }

  const single = value && !Array.isArray(value) ? value : null;
  return (
    <Select
      label={label}
      disabled={disabled}
      error={error}
      options={options}
      placeholder={placeholder}
      helpText={description}
      onSearch={onSearch}
      missingOptionsText={loading ? 'Loading…' : 'No matches'}
      selectedOption={single ? toOption(single) : null}
      onChange={(option) => onChange(option ? toValue(option) : null)}
    />
  );
};

export default AutocompleteField;
